import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Flex, Spinner, Text } from '@radix-ui/themes';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DateFieldComponent from '../../components/inputs/Datefield/DatefieldComponent';
import { DragAndDropFileUpload } from '../../components/inputs/FileUpload/DragAndDropFileUpload';
import TextAreaFieldComponent from '../../components/inputs/TextAreafield/TextAreafieldComponent';
import { useAppDispatch } from '../../hooks/hooks';
import { resetLessonPlanData } from '../../redux/slices/school/lessonPlan';
import { RootState } from '../../redux/store';
import { routes } from '../../routes';
import { createLessonPlanDocument } from '../../services/school/lessonPlans/createLessonPlan';
import { updateLessonPlan, updateLessonPlanDocument } from '../../services/school/lessonPlans/updateLessonPlan';
import { LessonPlanDocumentType, LessonPlanType } from '../../types/LessonPlan';
import { dataURLtoFile, getFile } from '../../utils/fileUtils';
import { CreateLessonPlanFormValidation } from './CreateLessonPlanFormValidation';
import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';

export const UpdateLessonPlan = () => {
  const lessonPlanData = useSelector((state: RootState) => state.lessonPlan);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const documents = lessonPlanData.documents?.map((document: LessonPlanDocumentType) => {
    if (document.docBase64) {
      const file = dataURLtoFile(document.docBase64, document.filename);
      return { ...document, file, docBase64: "" };
    }
    return { ...document, docBase64: "" };
  });

  const defaultValues = { ...lessonPlanData, documents };
  const mutationCreateLessonPlanDocument = useMutation(createLessonPlanDocument);
  const mutationUpdateLessonPlan = useMutation(updateLessonPlan);
  const mutationUpdateLessonPlanDocument = useMutation(updateLessonPlanDocument);
  const queryClient = useQueryClient();

  const { control, handleSubmit, setValue, getValues, formState: { errors }, reset } = useForm<LessonPlanType>({
    resolver: yupResolver(CreateLessonPlanFormValidation),
    defaultValues,
    mode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await mutationUpdateLessonPlan.mutateAsync(data);
      if (result) {
        if (data.documents && data.documents?.length > 0) {
          const newDocuments = data.documents.filter(d => !d.id);
          if(newDocuments && newDocuments.length===0){
            toast.success("Plano de aula atualizado com sucesso!");
            return;
          }
          const convertedDocs = await Promise.all(newDocuments.map(async (document) => {
            const file = document.file as File;
            return getFile(file, file.name);
          }));
          await Promise.all(convertedDocs.map(cv =>
            mutationCreateLessonPlanDocument.mutateAsync({
              docBase64: cv.base64,
              filename: cv.name,
              status: true,
              idLessonPlan: result,
              description: "",
            })
          ));
        }
        toast.success("Plano de aula atualizado com sucesso!");
        handleClickBack();
      }
    } catch (err) {
      toast.error("Erro ao atualizar o plano de aula.");
    }
  });

  const handleClickBack = () => {
    navigate(routes.manageLessonPlans);
    dispatch(resetLessonPlanData());
  };

    const removeFile = async (lessonPlanDocument: LessonPlanDocumentType) => {
        try {
            if(lessonPlanDocument.idLessonPlan){
                lessonPlanDocument.status = false;
                await mutationUpdateLessonPlanDocument.mutateAsync(lessonPlanDocument);
            }
        } catch (error) {
            console.error({ error })
        }
    };

  return (
    <Flex direction="column" gap="4">
      <form onSubmit={onSubmit} noValidate>
        <Flex direction="column" gap="4">
          <TextAreaFieldComponent name="objectives" label="Objectivos" control={control} errors={errors} />
          <TextAreaFieldComponent name="activities" label="Atividades" control={control} errors={errors} />
          <TextAreaFieldComponent name="resources" label="Recursos" control={control} errors={errors} />
          <DateFieldComponent name="date" label="Data" control={control} errors={errors} />

          <Flex direction="column" gap="3">
            <Text as="label">Documentos</Text>
            {fields.map((f, index) => (
              <Box
               key={index}
               style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '5px', marginBottom: '10px', position: 'relative' }}
               >
                   <Controller
                        name={`documents.${index}.file` as const}
                        control={control}
                        render={({ field }) => (
                          <DragAndDropFileUpload
                            onFileDrop={(file) => {
                              field.onChange(file);
                            }}
                            accept="*"
                            selectedFile={field.value}
                            remove={async() => {
                             await removeFile(getValues(`documents.${index}`));
                              remove(index);
                            }}
                          />
                        )}
                      />
                    {errors.documents?.[index]?.file && (
                        <Text color="red" size="1" style={{ marginTop: '5px' }}>
                          {`${errors.documents[index]?.file?.message}`}
                        </Text>
                      )}
                <Text
                  color="red"
                  size="1"
                  style={{ cursor: 'pointer', position: 'absolute', top: '10px', right: '10px', fontWeight: 'bold' }}
                  onClick={async () => {
                    await removeFile(getValues(`documents.${index}`));
                    remove(index);
                  }}
                >
                  Remover
                </Text>
              </Box>
            ))}
            <Button type="button" variant="surface" color="blue" onClick={() => append({ file: null })}>
              Adicionar Documento
            </Button>
          </Flex>

       <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                  <Button color='red' onClick={handleClickBack} disabled={mutationUpdateLessonPlan.isLoading}>
                    <ChevronLeftIcon /> voltar
                  </Button>
                  <Button type="submit" disabled={mutationUpdateLessonPlan.isLoading} variant='surface'>
                    {mutationUpdateLessonPlan.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                    Actualizar
                  </Button>
                </Flex>
        </Flex>
      </form>
    </Flex>
  );
};
